import { APIService } from "shared/services";
import { API_ENDPOINTS, API_ERROR_CODES } from "shared/constants";
import { IDataKeys, IJSONAPIItem, IAPIError } from "shared/types";
import { IEventPeopleBlockParams, IPeopleBlockParams, PeopleBlockStatus } from "Bands/types";

interface IJSONAPIPeopleBlock {
  [key: string]: IJSONAPIItem;
}

export interface IJSONAPIPeopleBlockCollection {
  [IDataKeys.eventPeopleBlock]: IJSONAPIPeopleBlock;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export interface IJSONAPIEventRSVPCollection {
  [IDataKeys.eventPeopleBlockUserRsvp]: IJSONAPIPeopleBlock;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export interface IJSONAPIPeopleBlockUserCollection {
  [IDataKeys.eventPeopleBlockUser]: IJSONAPIPeopleBlock;
  meta: {
    [key: string]: IJSONAPIItem;
  };
  pagination: any;
}

export class EventPeopleBlockService {
  static getBlocks = (
    queryParams?: string | { [key: string]: string | number | undefined | string[] }
  ): Promise<IJSONAPIPeopleBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const peopleBlocks: unknown = await APIService.get({
          path: API_ENDPOINTS.EVENT_PEOPLE_BLOCKS,
          data: queryParams,
        });
        resolve(peopleBlocks as IJSONAPIPeopleBlockCollection);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_PEOPLE_BLOCKS);
        return reject(error);
      }
    });

  static getBlock = (id: string): Promise<IJSONAPIPeopleBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.get({
          path: API_ENDPOINTS.EVENT_PEOPLE_BLOCK(id),
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_PEOPLE_BLOCK);
        return reject(error);
      }
    });

  static createBlock = (data: IEventPeopleBlockParams): Promise<IJSONAPIPeopleBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.post({
          path: API_ENDPOINTS.EVENT_PEOPLE_BLOCKS,
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_EVENT_PEOPLE_BLOCK);
        return reject(error);
      }
    });

  static editBlockTitle = ({
    id,
    title,
  }: {
    id: string;
    title: string;
  }): Promise<IJSONAPIPeopleBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_PEOPLE_BLOCK(id),
          data: { title },
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_PEOPLE_BLOCK_TITLE);
        return reject(error);
      }
    });

  static editBlock = ({
    id,
    data,
  }: {
    id: string;
    data: IEventPeopleBlockParams;
  }): Promise<IJSONAPIPeopleBlock> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_PEOPLE_BLOCK(id),
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_PEOPLE_BLOCK);
        return reject(error);
      }
    });

  static deleteBlock = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.EVENT_PEOPLE_BLOCK(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_EVENT_PEOPLE_BLOCK);
        return reject(error);
      }
    });

  static addMembers = ({
    id,
    data,
  }: {
    id: string;
    data: IPeopleBlockParams;
  }): Promise<IJSONAPIPeopleBlockCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_PEOPLE_BLOCK_ADD(id),
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_PEOPLE_BLOCK_MEMBER);
        return reject(error);
      }
    });

  static editUser = ({
    id,
    data,
  }: {
    id: string;
    data: IPeopleBlockParams;
  }): Promise<IJSONAPIPeopleBlockUserCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.patch({
          path: API_ENDPOINTS.EVENT_PEOPLE_BLOCK_USER(id),
          data,
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.PATCH_EVENT_PEOPLE_BLOCK_USER);
        return reject(error);
      }
    });

  static deleteUser = (id: string): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.delete({
          path: API_ENDPOINTS.EVENT_PEOPLE_BLOCK_USER(id),
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.DELETE_EVENT_PEOPLE_BLOCK_USER);
        return reject(error);
      }
    });

  static getRSVP = (id: string): Promise<IJSONAPIEventRSVPCollection> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await APIService.get({
          path: API_ENDPOINTS.EVENT_RSVP(id),
        });
        resolve(response);
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.GET_EVENT_RSVP);
        return reject(error);
      }
    });

  static answerRSVP = ({ id, status }: { id: string; status: PeopleBlockStatus }): Promise<void> =>
    new Promise(async (resolve, reject) => {
      try {
        await APIService.post({
          path: API_ENDPOINTS.ANSWER_EVENT_RSVP(id),
          isJSON: true,
          data: { status },
        });
        resolve();
      } catch (error) {
        (error as IAPIError).errorCodes?.push(API_ERROR_CODES.POST_ANSWER_EVENT_RSVP);
        return reject(error);
      }
    });
}
